<template>
  <el-dialog
    custom-class="batch-create-dialog"
    :visible="isOpen"
    :close-on-click-modal="false"
    :show-close="false"
    :destroy-on-close="true"
    width="640px"
    top="6vh"
    @update:visible="(v) => $emit('input', v)"
    @close="closeRequest"
  >
    <div v-if="program" class="batch-create">

      <!-- Topbar -->
      <div class="bc-topbar">
        <div class="bc-topbar-left">
          <button type="button" class="bc-close-btn" @click="closeRequest" aria-label="Close">
            <i class="el-icon-close"/>
          </button>
          <div class="bc-title-block">
            <div class="qd-eyebrow">Start a new run</div>
            <div class="bc-program-title">{{ program.title || 'Untitled program' }}</div>
          </div>
        </div>
        <!-- Topbar actions live in the footer only — duplicating them here
             made the small dialog feel cluttered. -->
      </div>

      <!-- Body -->
      <div class="bc-body">

        <!-- Batch name -->
        <section class="bc-card">
          <div class="bc-card-head">
            <label class="qd-eyebrow" for="batch-name">Run name</label>
          </div>
          <el-input
            id="batch-name"
            v-model="name"
            placeholder="e.g., First 30 days — November 2026"
          />
        </section>

        <!-- Start date -->
        <section class="bc-card">
          <div class="bc-card-head">
            <label class="qd-eyebrow" for="batch-start-date">Start date</label>
          </div>
          <el-date-picker
            id="batch-start-date"
            v-model="startDate"
            type="date"
            format="MMM d, yyyy"
            value-format="yyyy-MM-dd"
            placeholder="Pick a start date"
            class="bc-date-picker"
          />
          <p class="bc-helper-text">
            Day 1 is the start date. Day 2 is the next day, and so on.
          </p>
        </section>

        <!-- Schedule preview — makes "Day N" concrete so the author can
             confirm which calendar date each item will land on before
             running the batch. -->
        <section v-if="scheduledItems.length" class="bc-card">
          <div class="bc-card-head">
            <span class="qd-eyebrow">Schedule preview</span>
            <span class="bc-schedule-count">
              {{ scheduledItems.length }} item{{ scheduledItems.length === 1 ? '' : 's' }}
            </span>
          </div>
          <ul class="bc-schedule-list">
            <li v-for="row in scheduledItems" :key="row.id" class="bc-schedule-row">
              <span class="bc-schedule-day">{{ row.dayLabel }}</span>
              <span class="bc-schedule-date">{{ row.dateLabel }}</span>
              <span class="bc-schedule-type">{{ row.typeLabel }}</span>
              <span v-if="row.title" class="bc-schedule-title">· {{ row.title }}</span>
            </li>
          </ul>
        </section>

        <!-- Audience -->
        <section class="bc-card">
          <enrollment-picker :value="selections" @input="(s) => selections = s" />
        </section>

      </div>

      <!-- Footer -->
      <div class="bc-footer">
        <span v-if="createError" class="bc-create-error" role="alert">
          <i class="el-icon-warning-outline"/> {{ createError }}
        </span>
        <span v-else class="bc-readiness-hint">{{ readinessHint }}</span>
        <div class="bc-footer-actions">
          <el-button size="small" type="text" class="bc-cancel-btn" :disabled="creating" @click="closeRequest">Cancel</el-button>
          <el-button
            size="small"
            class="btn-6C30CC"
            :loading="creating"
            :disabled="!isValid || creating"
            @click="onCreate"
          >Create run</el-button>
        </div>
      </div>

    </div>
  </el-dialog>
</template>

<script>
import EnrollmentPicker from './enrollment-picker.vue'
import { dayLabel, formatScheduleDate, itemTypeLabel, todayIso } from './_program-labels'

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

function currentMonthLabel() {
  const d = new Date()
  return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`
}

export default {
  name: 'batch-create-modal',
  components: { EnrollmentPicker },
  props: {
    value: { type: Boolean, default: false },
    program: { type: Object, default: null },
    createHandler: { type: Function, required: true },
  },
  data() {
    return {
      name: '',
      startDate: todayIso(),
      selections: [],
      creating: false,
      createError: '',
    }
  },
  computed: {
    isOpen() {
      return Boolean(this.value && this.program)
    },
    isValid() {
      return (
        Boolean(this.name && this.name.trim()) &&
        Boolean(this.startDate) &&
        this.selections.length > 0
      )
    },
    readinessHint() {
      if (!this.name || !this.name.trim()) return 'Name the run'
      if (!this.startDate) return 'Pick a start date'
      if (!this.selections.length) return 'Add at least one person, team, or cohort'
      return 'Ready to create'
    },
    // Resolves "Day N" to a concrete calendar date relative to the
    // chosen startDate, mirroring processBatch's
    // `date(item) = baseStart + offsetDays`. Surfaces the schedule the
    // participant will actually experience so the author can confirm
    // before creating the batch.
    scheduledItems() {
      const items = (this.program && this.program.items) || []
      if (!this.startDate || items.length === 0) return []
      const base = new Date(`${this.startDate}T00:00:00`)
      if (Number.isNaN(base.getTime())) return []
      return [...items]
        .filter((it) => it && it.type)
        .sort((a, b) => (Number(a.offsetDays) || 0) - (Number(b.offsetDays) || 0))
        .map((it) => {
          const offsetDays = Number(it.offsetDays) || 0
          const d = new Date(base)
          d.setDate(d.getDate() + offsetDays)
          return {
            id: it.id,
            offsetDays,
            dayLabel: dayLabel(offsetDays),
            dateLabel: formatScheduleDate(d),
            typeLabel: itemTypeLabel(it.type),
            title: (it.participantTitle || '').trim(),
          }
        })
    },
  },
  watch: {
    value(open) {
      if (open) this.resetForOpen()
    },
    program() {
      if (this.value) this.resetForOpen()
    },
  },
  methods: {
    resetForOpen() {
      const title = (this.program && this.program.title) || 'Untitled program'
      this.name = `${title} — ${currentMonthLabel()}`
      this.startDate = todayIso()
      this.selections = []
      this.creating = false
      this.createError = ''
    },
    friendlyCreateError() {
      return "This run wasn't created. Your setup is still here — check your connection and try again."
    },
    async onCreate() {
      if (!this.isValid || !this.program || this.creating) return
      const payload = {
        programId: this.program.id,
        name: this.name.trim(),
        startDate: this.startDate,
        audienceSnapshot: {
          selections: this.selections.map((s) => ({
            id: s.id,
            kind: s.kind,
            name: s.name,
            meta: s.meta,
          })),
        },
      }
      this.creating = true
      this.createError = ''
      try {
        await this.createHandler(payload)
        this.$emit('input', false)
      } catch (error) {
        this.createError = this.friendlyCreateError(error)
      } finally {
        this.creating = false
      }
    },
    closeRequest() {
      if (this.creating) return
      this.$emit('input', false)
    },
  },
}
</script>

<style>
.batch-create-dialog {
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(108, 48, 204, 0.06), 0 24px 48px rgba(15, 16, 32, 0.18);
  overflow: hidden;
  max-width: calc(100vw - 32px);
}
.batch-create-dialog .el-dialog__header { display: none; }
.batch-create-dialog .el-dialog__body { padding: 0; }
</style>

<style scoped>
.batch-create {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 12vh);
}

/* Topbar */
.bc-topbar {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eef0f5;
  flex-shrink: 0;
}
.bc-topbar-left {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}
.bc-close-btn {
  appearance: none;
  background: transparent;
  border: 1px solid #e2e4ea;
  color: #6b7280;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  flex-shrink: 0;
  transition: border-color .15s ease, color .15s ease, background .15s ease;
}
.bc-close-btn:hover {
  border-color: rgba(108, 48, 204, 0.30);
  color: #1a1a1a;
  background: #f2ecfb;
}
.bc-title-block { min-width: 0; }
.bc-program-title {
  font-size: 16px;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.005em;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 360px;
}

/* Body */
.bc-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* Cards */
.bc-card {
  background: #fff;
  border: 1px solid #eef0f5;
  border-radius: 12px;
  padding: 20px 22px;
}
.bc-card-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 12px;
}


.bc-helper-text {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
  margin: 8px 0 0;
}

.bc-date-picker {
  width: 100%;
}

/* Footer */
.bc-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 20px;
  border-top: 1px solid #eef0f5;
  flex-shrink: 0;
  background: #fafbfd;
}
.bc-create-error {
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  color: #a32626;
  font-size: 12px;
  line-height: 1.4;
  max-width: 360px;
}
.bc-create-error i { margin-top: 2px; }
.bc-readiness-hint {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #8a8a8a;
}
.bc-footer-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Cancel text button — neutral tone, not brand purple */
.bc-cancel-btn {
  color: #6b7280;
  font-weight: 600;
  padding: 7px 10px;
}
.bc-cancel-btn:hover {
  color: #1a1a1a;
  background: transparent;
}

/* Schedule preview list — each row reads Day, calendar date, type,
   optional title. Brand purple for "Day N" anchors the eye; the rest
   stays neutral so the dates dominate. */
.bc-schedule-count {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #8a8a8a;
}
.bc-schedule-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.bc-schedule-row {
  display: flex;
  align-items: baseline;
  gap: 10px;
  font-size: 13px;
  color: #1a1a1a;
}
.bc-schedule-day {
  font-weight: 700;
  color: #6c30cc;
  min-width: 52px;
  flex-shrink: 0;
}
.bc-schedule-date {
  color: #1a1a1a;
  font-weight: 600;
  min-width: 104px;
  flex-shrink: 0;
}
.bc-schedule-type {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #8a8a8a;
  flex-shrink: 0;
}
.bc-schedule-title {
  color: #6b7280;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
</style>
